import React, { useEffect, useState } from "react";
import { getSuggestDoer, getSuggestCommunicator, getWhoCan, assignWork } from "../../../../api/hub/work";

// ─── AssignmentPopover ─────────────────────────────────────────────────────────
// Popover triggered by clicking "Unassigned" on a card or the assign button in
// the work panel. 3,885 unowned items is the problem this page exists to solve —
// so we do not offer a dropdown of nineteen names. We surface the top few
// suggestions with a plain-words `why`, and keep the full list one click away.
//
// Three parallel calls on mount, each fired as its own independent promise so a
// failure in one section never delays or sinks the others. The doer call gates
// the body (`loading`); communicator and whoCan each carry their own loading +
// error state so a missing fn_suggest_communicator (not in the schema cache yet)
// shows a clean empty state without touching the doer or capability sections.
//   • getSuggestDoer(clientId, workType)   → who should DO the work
//   • getSuggestCommunicator(clientId)     → who should TALK to the client
//   • getWhoCan(workType, clientId)        → who CAN do this type of work
//     (capability-based, derived from 102k completed tasks — separate from the
//     person-based getSuggestDoer)
//
// Response shape (fn_suggest_doer / fn_suggest_communicator) is not pinned in
// docs yet. Each row is assumed to carry { id, name, why }. Fallbacks to
// person_name / full_name / person_id / user_id are tolerated defensively —
// update here if the verified backend shape differs. Field-name mapping lives
// here, not in callers.
//
// Props: { clientId, workType, taskId, taskTitle, onSelect, onClose }
//   onSelect(personId) fires when a row is chosen (the parent routes this to
//   fn_work_assign for a single task or fn_work_bulk for a bulk reassign, then
//   closes + refreshes). When no onSelect is supplied but taskId is, the
//   popover calls assignWork(taskId, personId) directly. onClose() fires on
//   backdrop click or Escape (and after a selection).
const TOP_N = 3; // "top few" before the Show-all link

// teamMembers — the full team roster (same list that populates the Owner
// filter dropdown, i.e. allFilterOptions.owners in WorkPage.js). Rendered as
// the "All team members" section so the user can assign anyone, not just the
// system-recommended few. Optional: defaults to [] so the popover still works
// when WorkPage.js has not wired the prop yet.
const AssignmentPopover = ({ clientId, workType, taskId, taskTitle, onSelect, onClose, teamMembers = [] }) => {
  const [loading, setLoading] = useState(true);
  const [doers, setDoers] = useState([]);
  const [communicators, setCommunicators] = useState([]);
  const [commLoading, setCommLoading] = useState(true);
  const [doerError, setDoerError] = useState(null);
  const [commError, setCommError] = useState(false);
  const [showAllDoers, setShowAllDoers] = useState(false);
  const [showAllComms, setShowAllComms] = useState(false);
  // whoCan — capability-based suggestions (fn_who_can), independent of the
  // person-based doer/communicator suggestions above.
  const [whoCanResults, setWhoCanResults] = useState([]);
  const [whoCanLoading, setWhoCanLoading] = useState(true);
  const [whoCanError, setWhoCanError] = useState("");
  const [showAllWhoCan, setShowAllWhoCan] = useState(false);
  // Client-side search across every member row. Filters the Suggested and
  // "All team members" sections by name as the user types; empty shows all.
  const [search, setSearch] = useState("");

  // ── Load both suggestions in parallel on mount ──
  // allSettled so a single failure does not sink the other section.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setDoerError(null);
    setCommError(false);
    setCommLoading(true);
    setWhoCanError("");
    setWhoCanLoading(true);

    // doer — its own allSettled so a communicator/whoCan failure never delays
    // or sinks it. Controls the body gate (`loading`): the popover body appears
    // as soon as the doer call settles, regardless of the other two sections.
    Promise.allSettled([getSuggestDoer(clientId, workType)]).then(([dRes]) => {
      if (cancelled) return;

      if (dRes.status === "fulfilled") {
        const rows = dRes.value?.data;
        setDoers(Array.isArray(rows) ? rows : []);
      } else {
        setDoerError(dRes.reason?.message || "Could not load doer suggestions.");
      }

      setLoading(false);
    });

    // communicator — independent loading/error state. fn_suggest_communicator
    // may not exist in the schema cache yet (it rejects with "Could not find the
    // function public.fn_suggest_communicator…"). On rejection we show a clean
    // empty state ("Suggestions temporarily unavailable") and never surface the
    // raw Postgres error. Do NOT retry or call a fallback — this section just
    // shows a friendly message and leaves the doer / whoCan sections untouched.
    // Signature: getSuggestCommunicator(clientId, limit). clientId is passed
    // first so the RPC always receives p_client_id (null when the task has no
    // client) — omitting it raised PGRST202 on clientless tasks.
    getSuggestCommunicator(clientId, 5)
      .then((resp) => {
        if (cancelled) return;
        setCommunicators(resp?.data || []);
        setCommError(false);
        setCommLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setCommunicators([]);
        setCommError(true);
        setCommLoading(false);
      });

    // whoCan — capability-based lookup (fn_who_can). Fired in parallel but
    // resolved independently, so this section keeps its own loading state (the
    // spinner shows if fn_who_can is slower than the doer call). A failure here
    // never sinks the other sections.
    getWhoCan(workType, clientId)
      .then((resp) => {
        if (cancelled) return;
        setWhoCanResults(Array.isArray(resp?.data) ? resp.data : []);
        setWhoCanLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setWhoCanError(e?.message || "Failed to load");
        setWhoCanLoading(false);
      });

    return () => { cancelled = true; };
  }, [clientId, workType]);

  // ── Close on Escape ──
  // Document-level keydown listener; removed on unmount.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // ── Selection ──
  // Assigns the chosen person to the task. The person id comes from the
  // suggestion row's `member_id` (see getId). Prefer the parent's onSelect,
  // which already routes to fn_work_assign (single) or fn_work_bulk (bulk) and
  // refreshes the list. Fall back to a direct assignWork call when no onSelect
  // is supplied but a taskId prop is (a future single-task mount point).
  // Either way, close the popover after the assignment settles.
  const handleAssign = async (personId) => {
    if (personId == null) return;
    try {
      if (onSelect) {
        await onSelect(personId);
      } else if (taskId) {
        await assignWork(taskId, personId);
      }
      onClose?.();
    } catch (err) {
      console.error("Assignment failed:", err);
    }
  };

  // Defensive field access — see shape note at top of file.
  const getName = (s) => s?.name || s?.person_name || s?.full_name || "Unnamed";
  // whoCan (fn_who_can) rows historically carried the person id as auth_user_id,
  // but the verified response shape across all three suggestion RPCs uses
  // `member_id` as the person identifier — NOT id / user_id / auth_user_id.
  // member_id is read first so every section is enabled and reuses the same
  // handleAssign → onSelect/assignWork path.
  const getId = (s) => s?.member_id ?? s?.id ?? s?.auth_user_id ?? s?.person_id ?? s?.user_id;
  // Capability text for the whoCan section. fn_who_can response shape is not
  // pinned in docs yet — tolerate capability / capability_match / match, and
  // fall back to the same `why` field the other sections use. Update here if
  // the verified backend shape differs (field-name mapping lives here).
  const getCapability = (s) =>
    s?.capability || s?.capability_match || s?.match || s?.why || "";

  // Name-based client-side filter applied to every section. Lowercased
  // substring match against getName (tolerates the field-name fallbacks).
  const matchesSearch = (s) => {
    if (!search) return true;
    return getName(s).toLowerCase().includes(search.trim().toLowerCase());
  };

  const renderRow = (s, getSubtitle) => {
    const id = getId(s);
    const disabled = id == null;
    const subtitle = getSubtitle ? getSubtitle(s) : s?.why;
    return (
      <button
        type="button"
        key={id ?? Math.random()}
        disabled={disabled}
        onClick={() => {
          if (!disabled) handleAssign(id);
        }}
        className="w-full text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {getName(s)}
          </div>
          {subtitle && (
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-relaxed">
              {subtitle}
            </div>
          )}
        </div>
      </button>
    );
  };

  const renderSection = ({
    title,
    rows,
    error,
    showAll,
    setShowAll,
    loading = false,
    getSubtitle,
    emptyMessage = "No suggestions.",
  }) => (
    <div className="mb-4 last:mb-0">
      <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
        {title}
      </h4>

      {loading ? (
        // Independent loading spinner — only the whoCan section passes `loading`.
        <div className="py-3 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-[#32B1FF] rounded-full animate-spin" />
        </div>
      ) : error ? (
        // Muted error — never crashes the other section.
        <p className="text-xs text-gray-400 dark:text-gray-500 italic px-2 py-1.5">
          {error}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400 dark:text-gray-500 italic px-2 py-1.5">
          {emptyMessage}
        </p>
      ) : (
        <>
          <div className="space-y-0.5">
            {(showAll ? rows : rows.slice(0, TOP_N)).map((s) =>
              renderRow(s, getSubtitle)
            )}
          </div>
          {rows.length > TOP_N && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-1 ml-1 text-xs text-[#32B1FF] hover:underline"
            >
              {showAll ? "Show fewer" : `Show all (${rows.length})`}
            </button>
          )}
        </>
      )}
    </div>
  );

  // Backdrop mousedown closes the popover — but only when the press lands on
  // the backdrop itself (e.target === currentTarget), so clicks inside the
  // card do not bubble up and dismiss it. React removes this handler on unmount.
  const handleBackdropMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    // Semi-transparent backdrop, fixed and centered. Card is its only child so
    // flex centering places the card in the middle of the screen.
    <div
      role="presentation"
      onMouseDown={handleBackdropMouseDown}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40"
    >
      <div
        role="dialog"
        aria-label="Assignment suggestions"
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 max-h-[480px] overflow-y-auto w-[480px]"
      >
        {/* Header — title + optional task title, with the close button */}
        <div className="mb-4 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Assign task
            </h3>
            {taskTitle && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {taskTitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Search — filters every section below by name as the user types. */}
        <div className="mb-4">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search team members..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-[#32B1FF] transition-colors"
            />
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="py-6 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-[#32B1FF] rounded-full animate-spin" />
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              Finding the right people…
            </span>
          </div>
        ) : (
          <>
            {renderSection({
              title: "Suggested for this task",
              rows: doers.filter(matchesSearch),
              error: doerError,
              showAll: showAllDoers,
              setShowAll: setShowAllDoers,
              emptyMessage: search ? "No matches." : "No suggestions.",
            })}
            {renderSection({
              title: "Suggested communicator",
              rows: communicators.filter(matchesSearch),
              error: commError ? "Suggestions temporarily unavailable" : null,
              loading: commLoading,
              emptyMessage: search ? "No matches." : "No communicator suggestions available for this client",
              showAll: showAllComms,
              setShowAll: setShowAllComms,
            })}
            {/* All team members — full roster passed in as the teamMembers prop
                (the same list that populates the Owner filter dropdown in
                WorkPage.js). Every member is a clickable row that assigns on
                click via the same handleAssign → onSelect/assignWork path. The
                search input above filters this list by name alongside the
                Suggested sections. The section heading is ALWAYS rendered so
                the user knows the full roster lives here — even when the list
                is empty (e.g. no buckets loaded yet) we show a placeholder
                instead of hiding the section entirely. */}
            <div className="mb-4 last:mb-0">
              <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
                All Members
              </h4>
              {teamMembers.length === 0 ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 italic px-2 py-1.5">
                  No team members available yet — load a bucket to populate the roster.
                </p>
              ) : (
                <>
                  <div className="space-y-0.5 max-h-48 overflow-y-auto">
                    {teamMembers.filter(matchesSearch).map((m) => renderRow(m))}
                  </div>
                  {teamMembers.filter(matchesSearch).length === 0 && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 italic px-2 py-1.5">
                      No matches.
                    </p>
                  )}
                </>
              )}
            </div>
            {renderSection({
              title: "Who can do this type of work",
              rows: whoCanResults.filter(matchesSearch),
              error: whoCanError,
              loading: whoCanLoading,
              emptyMessage: search ? "No matches." : "No suggestions.",
              showAll: showAllWhoCan,
              setShowAll: setShowAllWhoCan,
              getSubtitle: getCapability,
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default AssignmentPopover;
